import React, { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { recruiterAPI, jobsAPI } from '../services/api';
import RecruiterSidebar from '../components/RecruiterSidebar';
import AnimatedTagline from '../components/AnimatedTagline';
import { MEDIA_URL, API } from '../config';
import '../styles/theme.css';
import './RecruiterCompanyView.css';

function RecruiterCompanyView() {
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();
  const [profile, setProfile] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [brandingMsg, setBrandingMsg] = useState('');
  const [brandColor, setBrandColor] = useState('');
  const [tagline, setTagline] = useState('');
  const [animateTagline, setAnimateTagline] = useState(false);
  const [coverUploading, setCoverUploading] = useState(false);
  const tier = localStorage.getItem('user_tier') || 'starter';
  const isBusiness = tier === 'business';

  const [logoUploading, setLogoUploading] = useState(false);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    try {
      const profileRes = await recruiterAPI.getProfile();
      const data = profileRes.data;
      if (data.company_logo && !data.company_logo.startsWith('http') && !data.company_logo.startsWith('data:')) {
        data.company_logo = `${MEDIA_URL}/${data.company_logo.replace(/^\/+/, '')}`;
      }
      setProfile(data);
      setBrandColor(data.brand_color || '#640ff7');
      setTagline(data.tagline || '');
      setAnimateTagline(data.animate_tagline || false);

      // Load this company's active jobs — use posted_by_user_id via my-jobs endpoint
      try {
        const token = localStorage.getItem('token');
        const jobsRes = await fetch(`${API}/jobs/my-jobs/`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (jobsRes.ok) {
          const allJobs = await jobsRes.json();
          setJobs(Array.isArray(allJobs) ? allJobs.filter(j => j.is_active !== false) : []);
        }
      } catch {}
    } catch (err) {
      if (err.response?.status === 404) navigate('/recruiter/profile-setup');
      else if (err.response?.status === 401) navigate('/recruiter/login');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLogoUploading(true);
    try {
      const res = await recruiterAPI.uploadLogo(file);
      const raw = res.data.logo_url;
      const logoUrl = raw.startsWith('http') ? raw : `${MEDIA_URL}/${raw.replace(/^\/+/, '')}`;
      setProfile(p => ({ ...p, company_logo: logoUrl }));
    } catch { alert('Failed to upload logo'); }
    finally { setLogoUploading(false); }
  };

  const handleCoverUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setCoverUploading(true);
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API}/recruiter-profile/me/cover/`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed');
      const url = data.cover_url.startsWith('http') ? data.cover_url : `${MEDIA_URL}${data.cover_url.startsWith('/') ? '' : '/'}${data.cover_url}`;
      setProfile(p => ({ ...p, cover_image: url }));
      setBrandingMsg('Cover image updated.');
    } catch (e) { setBrandingMsg(e.message); }
    finally { setCoverUploading(false); }
  };

  const handleSaveBranding = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/recruiter-profile/me/`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ brand_color: brandColor, tagline, animate_tagline: animateTagline }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed');
      setProfile(p => ({ ...p, brand_color: brandColor, tagline, animate_tagline: animateTagline }));
      setBrandingMsg('Branding saved.');
      setTimeout(() => setBrandingMsg(''), 3000);
    } catch (e) { setBrandingMsg(e.message); }
  };

  const getTypeBadge = (type) => {
    const map = { 'Full-time': 'badge-blue', 'Part-time': 'badge-orange', 'Contract': 'badge-purple', 'Internship': 'badge-green' };
    return map[type] || 'badge-gray';
  };

  if (loading) return (
    <div className="portal-layout">
      <RecruiterSidebar />
      <main className="portal-main">
        <div className="portal-content">
          <div className="empty-state">
            <i className="fas fa-spinner fa-spin" style={{ opacity: 1, color: 'var(--primary)' }}></i>
            <p>Loading company profile...</p>
          </div>
        </div>
      </main>
    </div>
  );

  if (!profile) return (
    <div className="portal-layout">
      <RecruiterSidebar />
      <main className="portal-main">
        <div className="portal-content">
          <div className="empty-state">
            <i className="fas fa-building"></i>
            <h3>No company profile found</h3>
            <p>Complete your profile setup to view your company page</p>
            <button className="btn btn-primary" style={{ marginTop: '1rem' }} onClick={() => navigate('/recruiter/profile-setup')}>
              Set Up Profile
            </button>
          </div>
        </div>
      </main>
    </div>
  );

  const completionPct = profile.profile_completion_percentage || 0;
  const planLabel = {
    starter: 'Starter', professional: 'Professional', business: 'Business'
  }[tier] || (profile.current_plan || 'Starter');

  return (
    <div className="portal-layout">
      <RecruiterSidebar />
      <main className="portal-main">
        <div className="portal-topbar">
          <span className="topbar-title">Company Profile</span>
          <div className="topbar-actions">
            <button className="topbar-btn" onClick={toggleTheme} title="Toggle theme"><i className={`fas ${isDark ? 'fa-sun' : 'fa-moon'}`}></i></button>
            <button className="btn btn-primary btn-sm" onClick={() => navigate('/recruiter/profile')}>
              <i className="fas fa-edit"></i> Edit Profile
            </button>
          </div>
        </div>

        <div className="portal-content">

          {/* Hero */}
          <div className="rcv-hero card" style={profile.cover_image ? { backgroundImage: `url(${profile.cover_image.startsWith('http') ? profile.cover_image : `${MEDIA_URL}${profile.cover_image.startsWith('/') ? '' : '/'}${profile.cover_image}`})`, backgroundSize: 'cover', backgroundPosition: 'center' } : {}}>
            <div className="rcv-hero-bg" style={profile.cover_image ? { opacity: 0.55 } : {}}>
              <div className="rcv-blob rcv-blob-1" style={profile.brand_color ? { background: profile.brand_color } : {}} />
              <div className="rcv-blob rcv-blob-2" style={profile.brand_color ? { background: profile.brand_color } : {}} />
              <div className="rcv-blob rcv-blob-3" />
            </div>
            <div className="rcv-hero-logo">
              {profile.company_logo
                ? <img src={profile.company_logo} alt={profile.company_name} className="rcv-logo-img" />
                : <div className="rcv-logo-placeholder">{profile.company_name?.charAt(0)}</div>
              }
              <label style={{ position: 'absolute', bottom: 0, right: 0, background: 'var(--primary)', borderRadius: '50%', width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.3)' }} title="Upload logo">
                {logoUploading
                  ? <i className="fas fa-spinner fa-spin" style={{ color: '#fff', fontSize: '0.75rem' }}></i>
                  : <i className="fas fa-camera" style={{ color: '#fff', fontSize: '0.75rem' }}></i>
                }
                <input type="file" accept="image/*" onChange={handleLogoUpload} style={{ display: 'none' }} />
              </label>
            </div>
            <div className="rcv-hero-info">
              <h1 className="rcv-company-name"><span className="rcv-animated-name">{profile.company_name}</span></h1>
              {profile.tagline && profile.animate_tagline ? (
                <p className="rcv-industry" style={{ margin: 0 }}>
                  <AnimatedTagline
                    tagline={profile.tagline}
                    wordClassName="rcv-tagline-word"
                  />
                </p>
              ) : (
                <p className="rcv-industry">{profile.tagline || profile.industry || 'Industry not specified'}</p>
              )}
              <div className="rcv-hero-badges">
                {profile.is_verified && (
                  <span className="badge badge-green"><i className="fas fa-check-circle"></i> Verified Employer</span>
                )}
                {profile.company_size && (
                  <span className="badge badge-blue"><i className="fas fa-users"></i> {profile.company_size} employees</span>
                )}
                {profile.city && (
                  <span className="badge badge-gray"><i className="fas fa-map-marker-alt"></i> {profile.city}{profile.province ? `, ${profile.province}` : ''}</span>
                )}
                <span className="badge badge-purple"><i className="fas fa-crown"></i> {planLabel} Plan</span>
              </div>
              {profile.company_website && (
                <a href={profile.company_website} target="_blank" rel="noopener noreferrer" className="rcv-website-link">
                  <i className="fas fa-globe"></i> {profile.company_website}
                </a>
              )}
            </div>

            {/* Completion ring */}
            <div className="rcv-completion">
              <svg viewBox="0 0 44 44" className="rcv-ring">
                <circle cx="22" cy="22" r="18" className="rcv-ring-bg" />
                <circle
                  cx="22" cy="22" r="18"
                  className="rcv-ring-fill"
                  strokeDasharray={`${(completionPct / 100) * 113.1} 113.1`}
                  strokeDashoffset="0"
                  transform="rotate(-90 22 22)"
                />
              </svg>
              <span className="rcv-completion-pct">{completionPct}%</span>
              <span className="rcv-completion-lbl">Profile Complete</span>
            </div>
          </div>

          {/* Incomplete profile nudge */}
          {completionPct < 80 && (
            <div className="alert alert-info rcv-nudge">
              <i className="fas fa-info-circle"></i>
              Your profile is {completionPct}% complete. A complete profile attracts more applicants.
              <button className="btn btn-primary btn-sm" style={{ marginLeft: 'auto' }} onClick={() => navigate('/recruiter/profile')}>
                Complete Profile
              </button>
            </div>
          )}

          <div className="rcv-body">

            {/* Custom Branding */}
            <div className="card" style={{ marginBottom: '1.5rem', gridColumn: '1 / -1' }}>
              <div className="card-header">
                <span className="card-title">
                  <i className="fas fa-paint-brush" style={{ color: 'var(--primary)', marginRight: '0.5rem' }}></i>
                  Custom Branding
                  <span className="badge badge-purple" style={{ marginLeft: '0.5rem', fontSize: '0.7rem' }}>Business</span>
                </span>
              </div>
              {!isBusiness ? (
                <div style={{ padding: '0.5rem 0', color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                  <i className="fas fa-lock" style={{ marginRight: '0.5rem' }}></i>
                  Custom branding is available on the Business plan.{' '}
                  <span style={{ color: 'var(--primary)', cursor: 'pointer', textDecoration: 'underline' }} onClick={() => navigate('/pricing')}>Upgrade</span>
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '1.25rem', paddingTop: '0.5rem' }}>
                  <div>
                    <label className="form-label">Cover / Banner Image</label>
                    <div style={{ position: 'relative', height: 80, borderRadius: 8, overflow: 'hidden', background: 'var(--surface-2)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {profile.cover_image
                        ? <img src={profile.cover_image.startsWith('http') ? profile.cover_image : `${MEDIA_URL}${profile.cover_image.startsWith('/') ? '' : '/'}${profile.cover_image}`} alt="cover" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        : <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>No cover image</span>}
                      <label style={{ position: 'absolute', bottom: 6, right: 6, background: 'var(--primary)', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontSize: '0.75rem', color: '#fff' }}>
                        {coverUploading ? <i className="fas fa-spinner fa-spin"></i> : <><i className="fas fa-upload"></i> Upload</>}
                        <input type="file" accept="image/*" onChange={handleCoverUpload} style={{ display: 'none' }} />
                      </label>
                    </div>
                    <div className="form-hint">Recommended: 1200×300px</div>
                  </div>
                  <div>
                    <label className="form-label">Brand Colour</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <input type="color" value={brandColor} onChange={e => setBrandColor(e.target.value)}
                        style={{ width: 48, height: 40, border: 'none', borderRadius: 8, cursor: 'pointer', padding: 2 }} />
                      <input className="form-control" value={brandColor} onChange={e => setBrandColor(e.target.value)}
                        placeholder="#640ff7" style={{ flex: 1 }} />
                    </div>
                    <div className="form-hint">Used for hero accents on your public profile</div>
                  </div>
                  <div>
                    <label className="form-label">Company Tagline</label>
                    <input className="form-control" value={tagline} onChange={e => setTagline(e.target.value)}
                      placeholder="e.g. Building the future of finance" maxLength={80} />
                    <div className="form-hint">Shown below your company name</div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.6rem', cursor: 'pointer', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                      <span style={{ position: 'relative', display: 'inline-block', width: 36, height: 20, flexShrink: 0 }}>
                        <input type="checkbox" checked={animateTagline} onChange={e => setAnimateTagline(e.target.checked)}
                          style={{ opacity: 0, width: 0, height: 0 }} />
                        <span style={{
                          position: 'absolute', cursor: 'pointer', inset: 0, borderRadius: 20,
                          background: animateTagline ? 'var(--primary)' : 'var(--border)', transition: '0.2s',
                        }}>
                          <span style={{
                            position: 'absolute', height: 14, width: 14,
                            left: animateTagline ? 19 : 3, bottom: 3,
                            background: '#fff', borderRadius: '50%', transition: '0.2s',
                          }} />
                        </span>
                      </span>
                      Animate tagline words
                    </label>
                    {animateTagline && tagline && (
                      <div style={{ marginTop: '0.5rem', padding: '0.5rem 0.75rem', background: 'var(--surface-2)', borderRadius: 6, fontSize: '0.82rem', color: 'var(--text-muted)' }}>
                        Preview: <AnimatedTagline tagline={tagline} wordClassName="rcv-tagline-word" style={{ color: 'var(--text-primary)', fontWeight: 500 }} />
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'flex-end', gap: '0.75rem' }}>
                    <button className="btn btn-primary" onClick={handleSaveBranding}>
                      <i className="fas fa-save"></i> Save Branding
                    </button>
                    {brandingMsg && <span style={{ fontSize: '0.82rem', color: '#10b981' }}>{brandingMsg}</span>}
                  </div>
                </div>
              )}
            </div>

            <div className="rcv-main">

              {/* About */}
              <div className="card rcv-section">
                <div className="card-header">
                  <span className="card-title"><i className="fas fa-info-circle" style={{ color: 'var(--primary)', marginRight: '0.5rem' }}></i>About {profile.company_name}</span>
                </div>
                {profile.about_company
                  ? <p className="rcv-text">{profile.about_company}</p>
                  : <p className="rcv-empty-field"><i className="fas fa-plus-circle"></i> Add a company description to attract candidates</p>
                }
              </div>

              {/* Mission */}
              {profile.mission_statement && (
                <div className="card rcv-section">
                  <div className="card-header">
                    <span className="card-title"><i className="fas fa-bullseye" style={{ color: 'var(--primary)', marginRight: '0.5rem' }}></i>Our Mission</span>
                  </div>
                  <p className="rcv-text rcv-mission">{profile.mission_statement}</p>
                </div>
              )}

              {/* Culture */}
              {profile.company_culture && (
                <div className="card rcv-section">
                  <div className="card-header">
                    <span className="card-title"><i className="fas fa-heart" style={{ color: 'var(--primary)', marginRight: '0.5rem' }}></i>Our Culture</span>
                  </div>
                  <p className="rcv-text">{profile.company_culture}</p>
                </div>
              )}

              {/* Values */}
              {profile.values && profile.values.length > 0 && (
                <div className="card rcv-section">
                  <div className="card-header">
                    <span className="card-title"><i className="fas fa-star" style={{ color: 'var(--primary)', marginRight: '0.5rem' }}></i>Our Values</span>
                  </div>
                  <div className="rcv-benefits">
                    {profile.values.map((v, i) => (
                      <span key={i} className="rcv-benefit-tag" style={{ background: 'rgba(99,102,241,0.12)', color: 'var(--primary)', border: '1px solid rgba(99,102,241,0.25)' }}>
                        <i className="fas fa-check"></i> {v}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Interview process */}
              {profile.interview_process && (
                <div className="card rcv-section">
                  <div className="card-header">
                    <span className="card-title"><i className="fas fa-comments" style={{ color: 'var(--primary)', marginRight: '0.5rem' }}></i>Interview Process</span>
                  </div>
                  <p className="rcv-text">{profile.interview_process}</p>
                  {profile.typical_hiring_timeline && (
                    <div style={{ marginTop: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <i className="fas fa-clock" style={{ color: 'var(--text-muted)' }}></i>
                      <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                        Typical timeline: <strong>{profile.typical_hiring_timeline}</strong>
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Industries hiring for */}
              {profile.industries_hiring_for && profile.industries_hiring_for.length > 0 && (
                <div className="card rcv-section">
                  <div className="card-header">
                    <span className="card-title"><i className="fas fa-briefcase" style={{ color: 'var(--primary)', marginRight: '0.5rem' }}></i>Industries We Hire For</span>
                  </div>
                  <div className="rcv-benefits">
                    {profile.industries_hiring_for.map((ind, i) => (
                      <span key={i} className="rcv-benefit-tag"><i className="fas fa-industry"></i> {ind}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Open positions */}
              <div className="card rcv-section">
                <div className="card-header">
                  <span className="card-title"><i className="fas fa-briefcase" style={{ color: 'var(--primary)', marginRight: '0.5rem' }}></i>Open Positions ({jobs.length})</span>
                  <button className="btn btn-ghost btn-sm" onClick={() => navigate('/recruiter/post-job')}>
                    <i className="fas fa-plus"></i> Post Job
                  </button>
                </div>
                {jobs.length === 0 ? (
                  <div className="empty-state" style={{ padding: '2rem 0' }}>
                    <i className="fas fa-briefcase"></i>
                    <p>No open positions yet</p>
                    <button className="btn btn-primary btn-sm" style={{ marginTop: '0.75rem' }} onClick={() => navigate('/recruiter/post-job')}>
                      Post Your First Job
                    </button>
                  </div>
                ) : (
                  <div className="rcv-jobs-list">
                    {jobs.map(job => (
                      <div key={job.id} className="rcv-job-row" onClick={() => navigate(`/recruiter/jobs/${job.id}/applications`)}>
                        <div className="rcv-job-info">
                          <span className="rcv-job-title">{job.title}</span>
                          <div className="rcv-job-meta">
                            {job.city && <span><i className="fas fa-map-marker-alt"></i> {job.city}</span>}
                            {job.work_mode && <span><i className="fas fa-laptop-house"></i> {job.work_mode}</span>}
                            {job.employment_type && <span><i className="fas fa-briefcase"></i> {job.employment_type}</span>}
                          </div>
                        </div>
                        <div className="rcv-job-right">
                          <span className={`badge ${getTypeBadge(job.employment_type)}`}>{job.employment_type || 'Full-time'}</span>
                          <span className="badge badge-gray"><i className="fas fa-users"></i> {job.application_count || 0}</span>
                          <i className="fas fa-chevron-right rcv-job-arrow"></i>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Right sidebar */}
            <div className="rcv-sidebar">

              {/* Quick stats */}
              <div className="card rcv-section">
                <div className="card-header">
                  <span className="card-title"><i className="fas fa-chart-bar" style={{ color: 'var(--primary)', marginRight: '0.5rem' }}></i>Quick Stats</span>
                </div>
                <div className="rcv-stats">
                  <div className="rcv-stat-row">
                    <span className="rcv-stat-label">Active Jobs</span>
                    <span className="rcv-stat-value">{jobs.filter(j => j.is_active).length}</span>
                  </div>
                  <div className="rcv-stat-row">
                    <span className="rcv-stat-label">Total Applications</span>
                    <span className="rcv-stat-value">{jobs.reduce((s, j) => s + (j.application_count || 0), 0)}</span>
                  </div>
                  <div className="rcv-stat-row">
                    <span className="rcv-stat-label">Plan</span>
                    <span className="rcv-stat-value">{profile.current_plan || 'Starter'}</span>
                  </div>
                  <div className="rcv-stat-row">
                    <span className="rcv-stat-label">Member Since</span>
                    <span className="rcv-stat-value">
                      {profile.created_at
                        ? new Date(profile.created_at).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
                        : 'Recently'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Benefits */}
              {profile.benefits && profile.benefits.length > 0 && (
                <div className="card rcv-section">
                  <div className="card-header">
                    <span className="card-title"><i className="fas fa-gift" style={{ color: 'var(--primary)', marginRight: '0.5rem' }}></i>Benefits</span>
                  </div>
                  <div className="rcv-benefits">
                    {profile.benefits.map((b, i) => (
                      <span key={i} className="rcv-benefit-tag">
                        <i className="fas fa-check"></i> {b}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Contact */}
              <div className="card rcv-section">
                <div className="card-header">
                  <span className="card-title"><i className="fas fa-address-card" style={{ color: 'var(--primary)', marginRight: '0.5rem' }}></i>Contact</span>
                </div>
                {profile.contact_person_name && (
                  <div className="rcv-contact-row"><i className="fas fa-user"></i><span>{profile.contact_person_name}{profile.contact_person_title ? ` · ${profile.contact_person_title}` : ''}</span></div>
                )}
                {profile.phone && (
                  <div className="rcv-contact-row"><i className="fas fa-phone"></i><span>{profile.phone}</span></div>
                )}
                {profile.city && (
                  <div className="rcv-contact-row">
                    <i className="fas fa-map-marker-alt"></i>
                    <span>{[profile.street_address, profile.city, profile.province, profile.postal_code].filter(Boolean).join(', ')}</span>
                  </div>
                )}
                {profile.company_website && (
                  <div className="rcv-contact-row">
                    <i className="fas fa-globe"></i>
                    <a href={profile.company_website} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)', textDecoration: 'none' }}>
                      {profile.company_website.replace(/^https?:\/\//, '')}
                    </a>
                  </div>
                )}
                {!profile.contact_person_name && !profile.phone && !profile.city && (
                  <p className="rcv-empty-field"><i className="fas fa-plus-circle"></i> Add contact details</p>
                )}
              </div>

              {/* Company details */}
              {(profile.industry || profile.company_size || profile.founded_year || profile.typical_hiring_timeline) && (
                <div className="card rcv-section">
                  <div className="card-header">
                    <span className="card-title"><i className="fas fa-building" style={{ color: 'var(--primary)', marginRight: '0.5rem' }}></i>Company Details</span>
                  </div>
                  {profile.industry && <div className="rcv-contact-row"><i className="fas fa-industry"></i><span>{profile.industry}</span></div>}
                  {profile.company_size && <div className="rcv-contact-row"><i className="fas fa-users"></i><span>{profile.company_size} employees</span></div>}
                  {profile.founded_year && <div className="rcv-contact-row"><i className="fas fa-calendar-alt"></i><span>Founded {profile.founded_year}</span></div>}
                  {profile.typical_hiring_timeline && !profile.interview_process && (
                    <div className="rcv-contact-row"><i className="fas fa-clock"></i><span>Hires in {profile.typical_hiring_timeline}</span></div>
                  )}
                </div>
              )}

              {/* Social links */}
              {(profile.linkedin_url || profile.facebook_url || profile.twitter_url || profile.instagram_url) && (
                <div className="card rcv-section">
                  <div className="card-header">
                    <span className="card-title"><i className="fas fa-share-alt" style={{ color: 'var(--primary)', marginRight: '0.5rem' }}></i>Social Media</span>
                  </div>
                  <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', paddingTop: '0.25rem' }}>
                    {profile.linkedin_url && (
                      <a href={profile.linkedin_url} target="_blank" rel="noopener noreferrer" style={{ color: '#0a66c2', fontSize: '0.875rem', textDecoration: 'none', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                        <i className="fab fa-linkedin" style={{ fontSize: '1.2rem' }}></i> LinkedIn
                      </a>
                    )}
                    {profile.facebook_url && (
                      <a href={profile.facebook_url} target="_blank" rel="noopener noreferrer" style={{ color: '#1877f2', fontSize: '0.875rem', textDecoration: 'none', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                        <i className="fab fa-facebook" style={{ fontSize: '1.2rem' }}></i> Facebook
                      </a>
                    )}
                    {profile.twitter_url && (
                      <a href={profile.twitter_url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--text-primary)', fontSize: '0.875rem', textDecoration: 'none', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                        <i className="fab fa-x-twitter" style={{ fontSize: '1.2rem' }}></i> X
                      </a>
                    )}
                    {profile.instagram_url && (
                      <a href={profile.instagram_url} target="_blank" rel="noopener noreferrer" style={{ color: '#e1306c', fontSize: '0.875rem', textDecoration: 'none', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                        <i className="fab fa-instagram" style={{ fontSize: '1.2rem' }}></i> Instagram
                      </a>
                    )}
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>

        {/* Floating Edit Button */}
        <button className="rcv-fab" onClick={() => navigate('/recruiter/profile')} title="Edit Profile">
          <i className="fas fa-edit"></i>
          <span>Edit</span>
        </button>
      </main>
    </div>
  );
}

export default RecruiterCompanyView;
